import { Box, Button, Checkbox, HStack, Image, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Radio, RadioGroup, Select, Spinner, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useDisclosure, useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChangeEvent, useMemo, useState } from "react";
import { adminDeleteChats, adminGetChats, adminUpdateChatHiddenReason, adminUpdateChatStatus } from "../../../api";
import AdminOnly from "../../../components/Auth/AdminOnly";
import MyIcon from "../../../components/Icon/MyIcon";
import ImageModal from "../../../components/Modals/ImageModal";
import TextModal from "../../../components/Modals/TextModal";
import { colors } from "../../../styles";
import { IAdminChat, IAdminChatList } from "../../../types";
import { formatDateTimeKor } from "../../../utils";

export default function Chats() {

    const [page, setPage] = useState(1);
    const size = 100;
    const [isReportedOnly, setIsReportedOnly] = useState<string>("all");
    const [isHiddenOnly, setIsHiddenOnly] = useState<string>("all");

    const reportedOnlyParam = useMemo(() => {
        if (isReportedOnly === "all") return undefined;
        return isReportedOnly === "true";
    }, [isReportedOnly]);

    const hiddenOnlyParam = useMemo(() => {
        if (isHiddenOnly === "all") return undefined;
        return isHiddenOnly === "true";
    }, [isHiddenOnly]);

    const handleIsReportedOnlyChange = (value: string) => {
        setPage(1);
        setIsReportedOnly(value);
    };

    const handleIsHiddenOnlyChange = (value: string) => {
        setPage(1);
        setIsHiddenOnly(value);
    };

    const { data: chatListData, isLoading: isChatListDataLoading } = useQuery<IAdminChatList>({
        queryKey: ['adminGetChats', page, size, reportedOnlyParam, hiddenOnlyParam],
        queryFn: () => adminGetChats({ page, size, isReportedOnly: reportedOnlyParam, isHiddenOnly: hiddenOnlyParam }),
        enabled: true,
    });

    const totalPages = useMemo(() => chatListData?.totalPages ?? 0, [chatListData]);
    const currentPage = useMemo(() => chatListData?.currentPage ?? 0, [chatListData]);

    const pages = useMemo(() => {
        const startPage = Math.max(page - 4, 1);
        const endPage = Math.min(page + 5, totalPages);
        return Array.from({ length: Math.max(0, endPage - startPage + 1) }, (_, idx) => startPage + idx);
    }, [page, totalPages]);

    const { isOpen: isImageModalOpen, onOpen: onOpenImageModal, onClose: onCloseImageModal } = useDisclosure();
    const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);

    const openImage = (url: string) => {
        setSelectedImageUrl(url);
        onOpenImageModal();
    };

    const closeImage = () => {
        setSelectedImageUrl(null);
        onCloseImageModal();
    };

    const { isOpen: isTextModalOpen, onOpen: onOpenTextModal, onClose: onCloseTextModal } = useDisclosure();
    const [selectedText, setSelectedText] = useState<string>("");

    const openText = (text: string) => {
        setSelectedText(text);
        onOpenTextModal();
    };

    const closeText = () => {
        setSelectedText("");
        onCloseTextModal();
    };

    const chatColumns = [
        { key: "profile.nickname", label: "👤 작성자" },
        { key: "content", label: "내용" },
        { key: "imageUrls", label: "이미지" },
        { key: "reports", label: "신고 수" },
        { key: "status", label: "상태" },
        { key: "hiddenReason", label: "숨김 사유" },
        { key: "createdAt", label: "생성 일자" },
        { key: "updatedAt", label: "수정 일자" },
    ];

    const [selectedChatIds, setSelectedChatIds] = useState<string[]>([]);
    const currentPageChatIds = useMemo(() => chatListData?.chats.map((chat) => chat.id) ?? [], [chatListData]);

    const isAllCurrentPageSelected = useMemo(() => {
        return currentPageChatIds.length > 0 && currentPageChatIds.every((id) => selectedChatIds.includes(id));
    }, [currentPageChatIds, selectedChatIds]);

    const isSomeCurrentPageSelected = useMemo(() => {
        const someSelected = currentPageChatIds.some((id) => selectedChatIds.includes(id));
        return someSelected && !isAllCurrentPageSelected;
    }, [currentPageChatIds, selectedChatIds, isAllCurrentPageSelected]);

    const toggleSelectAllCurrentPage = () => {
        if (isAllCurrentPageSelected) {
            setSelectedChatIds((prev) => prev.filter((id) => !currentPageChatIds.includes(id)));
        } else {
            setSelectedChatIds((prev) => Array.from(new Set([...prev, ...currentPageChatIds])));
        }
    };

    const toggleSelectOne = (chatId: string) => {
        setSelectedChatIds((prev) => (prev.includes(chatId) ? prev.filter((id) => id !== chatId) : [...prev, chatId]));
    };

    const { isOpen: isDeleteModalOpen, onOpen: onOpenDeleteModal, onClose: onCloseDeleteModal } = useDisclosure();

    const queryClient = useQueryClient();
    const toast = useToast();

    const deleteChatsMutation = useMutation({
        mutationFn: adminDeleteChats,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetChats'] });
            setSelectedChatIds([]);
        },
        onError: (error) => {
            console.error(error);
            toast({
                title: "채팅 삭제에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const onDeleteChats = () => {
        deleteChatsMutation.mutate({ chatIds: selectedChatIds });
        onCloseDeleteModal();
    };

    const updateChatStatusMutation = useMutation({
        mutationFn: adminUpdateChatStatus,
        onMutate: async (variables) => {
            const { chatId, status } = variables;
            await queryClient.cancelQueries({ queryKey: ['adminGetChats', page, size, reportedOnlyParam, hiddenOnlyParam] });
            const previous = queryClient.getQueryData<IAdminChatList>(['adminGetChats', page, size, reportedOnlyParam, hiddenOnlyParam]);
            if (previous) {
                const next = {
                    ...previous,
                    chats: previous.chats.map((c) => (
                        c.id === chatId
                            ? {
                                ...c,
                                status,
                                hiddenAt: status === "HIDDEN" ? (c.hiddenAt ?? new Date()) : null,
                            }
                            : c
                    )),
                };
                queryClient.setQueryData(['adminGetChats', page, size, reportedOnlyParam, hiddenOnlyParam], next);
            }
            return { previous };
        },
        onError: (_error, _variables, context) => {
            const prev = (context as { previous?: IAdminChatList })?.previous;
            if (prev) {
                queryClient.setQueryData(['adminGetChats', page, size, reportedOnlyParam, hiddenOnlyParam], prev);
            }
            toast({
                title: "상태 변경에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const updateChatHiddenReasonMutation = useMutation({
        mutationFn: adminUpdateChatHiddenReason,
        onMutate: async (variables) => {
            const { chatId, hiddenReason } = variables;
            await queryClient.cancelQueries({ queryKey: ['adminGetChats', page, size, reportedOnlyParam, hiddenOnlyParam] });
            const previous = queryClient.getQueryData<IAdminChatList>(['adminGetChats', page, size, reportedOnlyParam, hiddenOnlyParam]);
            if (previous) {
                const next = {
                    ...previous,
                    chats: previous.chats.map((c) => (
                        c.id === chatId ? { ...c, hiddenReason } : c
                    )),
                };
                queryClient.setQueryData(['adminGetChats', page, size, reportedOnlyParam, hiddenOnlyParam], next);
            }
            return { previous };
        },
        onError: (_error, _variables, context) => {
            const prev = (context as { previous?: IAdminChatList })?.previous;
            if (prev) {
                queryClient.setQueryData(['adminGetChats', page, size, reportedOnlyParam, hiddenOnlyParam], prev);
            }
            toast({
                title: "숨김 사유 저장에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    type ChatStatus = IAdminChat["status"];

    const statusDotColor = (status: ChatStatus) => {
        if (status === "HIDDEN") return colors.apple;
        if (status === "HOLD") return colors.yellow;
        return colors.green;
    };

    const statusComponent = (chatId: string, status: ChatStatus) => {
        return (
            <HStack justifyContent={"flex-start"} alignItems={"center"} width={"180px"} gap={2}>
                <Box
                    width={"8px"}
                    height={"8px"}
                    borderRadius={"4px"}
                    backgroundColor={statusDotColor(status)}
                />
                <Select
                    size="xs"
                    value={status}
                    onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                        const nextStatus = event.target.value as ChatStatus;
                        if (nextStatus !== status) {
                            updateChatStatusMutation.mutate({ chatId, status: nextStatus });
                        }
                    }}
                    isDisabled={updateChatStatusMutation.isPending}
                >
                    <option value="NORMAL">NORMAL</option>
                    <option value="HOLD">HOLD</option>
                    <option value="HIDDEN">HIDDEN</option>
                </Select>
            </HStack>
        );
    };

    function HiddenReasonCell({ chat }: { chat: IAdminChat }) {
        const [isEditing, setIsEditing] = useState(false);
        const [value, setValue] = useState<string>(String(chat.hiddenReason ?? ""));
        const canEditHiddenReason = chat.status === "HIDDEN";

        return (
            <HStack justifyContent={"space-between"} alignItems={"center"} width={"100%"} gap={2}>
                {canEditHiddenReason && isEditing ? (
                    <>
                        <Input
                            flex={"1"}
                            minW={"320px"}
                            size={"sm"}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                        />
                        <HStack flexShrink={0} gap={1}>
                            <Button
                                size={"xs"}
                                variant={"solid"}
                                backgroundColor={colors.gray}
                                borderRadius={10}
                                _hover={{ bg: colors.blue }}
                                boxShadow="sm"
                                onClick={async () => {
                                    await updateChatHiddenReasonMutation.mutateAsync({ chatId: chat.id, hiddenReason: value });
                                    setIsEditing(false);
                                }}
                            >
                                <Text fontSize={8} fontWeight={400} color={colors.white}>💾</Text>
                            </Button>
                            <Button
                                size={"xs"}
                                variant={"ghost"}
                                borderRadius={10}
                                onClick={() => {
                                    setValue(String(chat.hiddenReason ?? ""));
                                    setIsEditing(false);
                                }}
                            >
                                <Text fontSize={8} fontWeight={400} color={colors.black}>❌</Text>
                            </Button>
                        </HStack>
                    </>
                ) : (
                    <>
                        <Text flex={"1"} minW={"200px"} noOfLines={1} color={canEditHiddenReason ? undefined : colors.g3}>
                            {String(chat.hiddenReason ?? "-")}
                        </Text>
                        <HStack flexShrink={0} gap={1}>
                            <Button
                                size={"xs"}
                                variant={"solid"}
                                backgroundColor={colors.white}
                                borderRadius={10}
                                _hover={{ bg: canEditHiddenReason ? colors.blue : colors.white }}
                                boxShadow="sm"
                                isDisabled={!canEditHiddenReason}
                                onClick={() => {
                                    setValue(String(chat.hiddenReason ?? ""));
                                    setIsEditing(true);
                                }}
                            >
                                <Text fontSize={12} fontWeight={400} color={canEditHiddenReason ? colors.black : colors.g3}>🖋️</Text>
                            </Button>
                        </HStack>
                    </>
                )}
            </HStack>
        );
    }

    const renderCellValue = (chat: IAdminChat, columnKey: string) => {
        if (columnKey === "profile.nickname") {
            return String(chat.profile?.nickname ?? "-");
        }
        if (columnKey === "imageUrls") {
            if (chat.imageUrls.length === 0) {
                return "-";
            }
            return (
                <HStack spacing={2}>
                    {chat.imageUrls.map((imageUrl) => (
                        <Box
                            key={imageUrl}
                            width={8}
                            height={8}
                            borderRadius={2}
                            border={"1px solid " + colors.g5}
                            backgroundColor={colors.g5}
                            onClick={() => openImage(imageUrl)}
                            cursor={"pointer"}
                        >
                            <Image src={imageUrl} alt="chatImage" width={"100%"} height={"100%"} objectFit={"cover"} />
                        </Box>
                    ))}
                </HStack>
            );
        }
        if (columnKey === "reports") {
            return String(chat.reports.length);
        }
        if (columnKey === "content") {
            const content = chat.content ?? "-";
            const displayContent = content.length > 20 ? `${content.slice(0, 20)}...` : content;
            return (
                <Text cursor={"pointer"} onClick={() => openText(content)}>
                    {displayContent}
                </Text>
            );
        }
        if (columnKey === "status") {
            return statusComponent(chat.id, chat.status);
        }
        if (columnKey === "hiddenReason") {
            return <HiddenReasonCell chat={chat} />;
        }
        if (columnKey === "createdAt" || columnKey === "updatedAt") {
            return formatDateTimeKor(chat[columnKey]);
        }
        return String(chat[columnKey as keyof IAdminChat] ?? "-");
    };

    return (
        <AdminOnly>
            <Stack width={"100%"} padding={10}>
                <HStack justifyContent={"space-between"} alignItems={"center"} gap={10}>
                    <HStack justifyContent={"flex-start"} alignItems={"center"} gap={3}>
                        <Text fontSize={28} fontWeight={700} color={colors.black}>채팅</Text>
                        <Text fontSize={28} fontWeight={400} color={colors.gray}>{chatListData?.totalCount}</Text>
                    </HStack>
                    <HStack gap={3} />
                </HStack>

                <RadioGroup value={isReportedOnly} onChange={handleIsReportedOnlyChange}>
                    <HStack spacing={6} marginTop={4}>
                        <Box
                            paddingX={3}
                            paddingY={1}
                            backgroundColor={colors.light}
                            borderRadius={10}

                            width={"130px"}
                            textAlign={"center"}
                        >
                            <Text fontSize={12} fontWeight={500} color={colors.blue}>REPORTEDONLY</Text>
                        </Box>
                        <Radio value="all" colorScheme="blue">모두</Radio>
                        <Radio value="true" colorScheme="blue">TRUE</Radio>
                        <Radio value="false" colorScheme="blue">FALSE</Radio>
                    </HStack>
                </RadioGroup>

                <RadioGroup value={isHiddenOnly} onChange={handleIsHiddenOnlyChange}>
                    <HStack spacing={6} marginBottom={4}>
                        <Box
                            paddingX={3}
                            paddingY={1}
                            backgroundColor={colors.light}
                            borderRadius={10}

                            width={"130px"}
                            textAlign={"center"}
                        >
                            <Text fontSize={12} fontWeight={500} color={colors.blue}>HIDDENEDONLY</Text>
                        </Box>
                        <Radio value="all" colorScheme="blue">모두</Radio>
                        <Radio value="true" colorScheme="blue">TRUE</Radio>
                        <Radio value="false" colorScheme="blue">FALSE</Radio>
                    </HStack>
                </RadioGroup>

                <HStack justifyContent={"flex-start"} marginY={0}>
                    <Button
                        size={"sm"}
                        variant={"solid"}
                        isDisabled={selectedChatIds.length === 0}
                        opacity={selectedChatIds.length === 0 ? 0.1 : 1}
                        onClick={onOpenDeleteModal}
                    >
                        <MyIcon src={"/svg/trash-filled.svg"} alt="trash" width={16} height={16} />
                    </Button>
                    <Modal isOpen={isDeleteModalOpen} onClose={onCloseDeleteModal}>
                        <ModalOverlay />
                        <ModalContent>
                            <ModalHeader>
                                <Text fontSize={20} fontWeight={700} color={colors.black}>삭제하기</Text>
                                <ModalCloseButton />
                            </ModalHeader>
                            <ModalBody>
                                <Text fontSize={16} fontWeight={400} color={colors.black}>선택된 행 수: {selectedChatIds.length}</Text>
                                <Text fontSize={16} fontWeight={400} color={colors.black}>정말 삭제하시겠습니까?</Text>
                            </ModalBody>
                            <Stack marginTop={5} width={"100%"} padding={3}>
                                <Button
                                    onClick={onDeleteChats}
                                    isLoading={deleteChatsMutation.isPending}
                                    width={"100%"}
                                    backgroundColor={colors.blue}
                                    color={colors.white}
                                    borderRadius={10}
                                    _hover={{ bg: colors.blue }}
                                    boxShadow="sm"
                                >
                                    삭제
                                </Button>
                            </Stack>
                        </ModalContent>
                    </Modal>
                </HStack>

                {!isChatListDataLoading ? (
                    <TableContainer marginTop={5}>
                        <Table variant='simple' scrollBehavior="smooth">
                            <Thead backgroundColor={colors.g5}>
                                <Tr>
                                    <Th>
                                        <Checkbox
                                            isChecked={isAllCurrentPageSelected}
                                            isIndeterminate={isSomeCurrentPageSelected}
                                            onChange={toggleSelectAllCurrentPage}
                                        />
                                    </Th>
                                    {chatColumns.map((column) => (
                                        <Th key={column.key}>{column.label}</Th>
                                    ))}
                                </Tr>
                            </Thead>
                            <Tbody>
                                {chatListData?.chats.map((chat) => (
                                    <Tr key={chat.id}>
                                        <Td>
                                            <Checkbox
                                                isChecked={selectedChatIds.includes(chat.id)}
                                                onChange={() => toggleSelectOne(chat.id)}
                                            />
                                        </Td>
                                        {chatColumns.map((column) => (
                                            <Td key={column.key}>
                                                {renderCellValue(chat, column.key)}
                                            </Td>
                                        ))}
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Spinner color={colors.blue} />
                )}

                <HStack justifyContent={"center"} marginTop={5}>
                    {page > 1 && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page - 1)}>Previous</Button>
                    )}

                    {page > 5 && (
                        <>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(1)}>1</Button>
                            <Text>...</Text>
                        </>
                    )}

                    {pages.map((pageNumber) => (
                        <Button
                            key={pageNumber}
                            size={"xs"} variant={"ghost"}
                            onClick={() => setPage(pageNumber)}
                            backgroundColor={pageNumber === currentPage ? colors.blue : ""}
                            color={pageNumber === currentPage ? colors.white : ""}
                        >
                            {pageNumber}
                        </Button>
                    ))}

                    {page < totalPages - 4 && (
                        <>
                            <Text>...</Text>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(totalPages)}>{totalPages}</Button>
                        </>
                    )}

                    {page < totalPages && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page + 1)}>Next</Button>
                    )}
                </HStack>
            </Stack>

            {selectedImageUrl &&
                <ImageModal
                    isOpen={isImageModalOpen}
                    onClose={closeImage}
                    imageUrl={selectedImageUrl}
                />
            }

            <TextModal
                isOpen={isTextModalOpen}
                onClose={closeText}
                text={selectedText}
            />
        </AdminOnly>
    );
}
